import { authFetch } from './authFetch';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

export interface SyncTimelineEvent {
    at: string | null;
    type: string | null;
    attempt: number | null;
    from_status: string | null;
    to_status: string | null;
    stage: string | null;
    code: string | null;
    msg: string | null;
}

export interface SyncTrackingItem {
    id: number;
    entity_type: string;
    direction: string;
    operation: string;
    trigger: string;
    emr_id: string;
    ghl_id: string;
    patient_id: number | null;
    contact_id: number | null;
    appointment_id: number | null;
    sync_run_id: number | null;
    depends_on_id: number | null;
    status: string;
    stage: string;
    source_version: string;
    deduplication_key: string;
    attempt_count: number;
    max_attempts: number;
    next_retry_at: string | null;
    failure_code: string;
    error_class: string;
    error_message: string;
    processing_started_at: string | null;
    completed_at: string | null;
    last_seen_at: string | null;
    created_at: string | null;
    updated_at: string | null;
    timeline: SyncTimelineEvent[];
    comments: string;
}

export interface SyncTrackingResponse {
    items: SyncTrackingItem[];
    total: number;
}

export interface SyncTrackingStatsResponse {
    counts: Record<string, number>;
}

export type SyncBriefCategory = 'configuration' | 'data' | 'temporary' | 'permanent';

export interface SyncBriefTheme {
    title: string;
    category: SyncBriefCategory;
    failure_codes: string[];
    affected_count: number;
    explanation: string;
    recommended_action: string;
    self_healing: boolean;
}

export interface SyncBriefCode {
    failure_code: string;
    count: number;
    exhausted_retries: number;
    last_seen: string | null;
}

export interface SyncBriefResponse {
    total_failing: number;
    distinct_codes: number;
    headline: string;
    summary: string;
    themes: SyncBriefTheme[];
    next_steps: string[];
    top_codes: SyncBriefCode[];
    llm_model: string | null;
}

export interface SyncTrackingFilters {
    q?: string;
    entity?: 'patient' | 'appointment';
    direction?: string;
    status?: string;
    trigger?: string;
    group?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
}

export interface SyncAnalyticsSlice {
    key: string;
    label: string;
    value: number;
}

export interface SyncAnalyticsDailyPoint {
    date: string;
    label: string;
    patients: number;
    appointments: number;
    emr_to_ghl: number;
    ghl_to_emr: number;
    succeeded: number;
    failed: number;
    ignored: number;
    in_progress: number;
    retrying: number;
    permanent_failed: number;
}

export interface SyncAnalyticsKpis {
    total_synced: number;
    success_rate: number;
    failed: number;
    in_progress: number;
    avg_sync_seconds: number | null;
    patients_synced: number;
    appointments_synced: number;
}

export interface SyncAnalyticsResponse {
    daily: SyncAnalyticsDailyPoint[];
    kpis: SyncAnalyticsKpis;
    outcomes: SyncAnalyticsSlice[];
    directions: SyncAnalyticsSlice[];
    entities: SyncAnalyticsSlice[];
    peak: SyncAnalyticsDailyPoint | null;
}

export interface SyncAnalyticsDetailItem {
    id: number;
    label: string;
    entity: string;
    direction: string;
    status: string;
    status_label: string;
    failure_code: string;
    failure_label: string;
    error_message: string;
    mapping_route: string | null;
    mapping_cta: string | null;
}

export interface SyncAnalyticsMappingAction {
    key: string;
    label: string;
    count: number;
    mapping_route: string;
    mapping_cta: string;
    brief: string;
}

export interface SyncAnalyticsDetailResponse {
    title: string;
    subtitle: string;
    scope: string;
    scope_key: string;
    total: number;
    shown: number;
    items: SyncAnalyticsDetailItem[];
    mapping_actions: SyncAnalyticsMappingAction[];
}

export interface SyncAnalyticsFilters {
    dateFrom: string;
    dateTo: string;
    entity?: 'patient' | 'appointment';
    direction?: 'emr_to_ghl' | 'ghl_to_emr';
}

export type AnalyticsDetailScope = 'day' | 'outcome' | 'direction' | 'entity';

export interface SyncAnalyticsDetailFilters extends SyncAnalyticsFilters {
    scope: AnalyticsDetailScope;
    scopeKey: string;
}

/** Raised when the engine DB is not configured (HTTP 503). */
export class EngineUnavailableError extends Error {}

export const syncTrackingApi = {
    async list(locationId: string, emr: string, filters?: SyncTrackingFilters): Promise<SyncTrackingResponse> {
        const params = new URLSearchParams({ location_id: locationId, emr });
        if (filters?.q) params.append('q', filters.q);
        if (filters?.entity) params.append('entity', filters.entity);
        if (filters?.direction) params.append('direction', filters.direction);
        if (filters?.status) params.append('status', filters.status);
        if (filters?.trigger) params.append('trigger', filters.trigger);
        if (filters?.group) params.append('group', filters.group);
        if (filters?.dateFrom) params.append('date_from', filters.dateFrom);
        if (filters?.dateTo) params.append('date_to', filters.dateTo);
        if (filters?.limit !== undefined) params.append('limit', String(filters.limit));
        if (filters?.offset !== undefined) params.append('offset', String(filters.offset));

        const response = await authFetch(`${API_BASE_URL}/sync-tracking?${params.toString()}`);
        if (response.status === 503) {
            throw new EngineUnavailableError('Engine DB is not configured');
        }
        if (!response.ok) {
            const detail = await response.json().catch(() => ({}));
            throw new Error(detail?.detail || 'Failed to load sync tracking');
        }
        return response.json();
    },

    async stats(locationId: string, emr: string): Promise<SyncTrackingStatsResponse> {
        const params = new URLSearchParams({ location_id: locationId, emr });
        const response = await authFetch(`${API_BASE_URL}/sync-tracking/stats?${params.toString()}`);
        if (response.status === 503) {
            throw new EngineUnavailableError('Engine DB is not configured');
        }
        if (!response.ok) {
            const detail = await response.json().catch(() => ({}));
            throw new Error(detail?.detail || 'Failed to load sync tracking stats');
        }
        return response.json();
    },

    /**
     * Generate a Gemini brief on the location's most common sync failures.
     * Always covers every failing record, ignoring the table's active filters.
     */
    async aiBrief(locationId: string, emr: string): Promise<SyncBriefResponse> {
        const params = new URLSearchParams({ location_id: locationId, emr });
        const response = await authFetch(`${API_BASE_URL}/sync-tracking/ai-brief?${params.toString()}`, {
            method: 'POST',
        });
        if (response.status === 503) {
            throw new EngineUnavailableError('Engine DB is not configured');
        }
        if (!response.ok) {
            const detail = await response.json().catch(() => ({}));
            throw new Error(detail?.detail || 'Failed to generate AI brief');
        }
        return response.json();
    },

    /**
     * Daily aggregates + KPI slices for Sync Analytics (practice-scoped).
     */
    async analytics(locationId: string, emr: string, filters: SyncAnalyticsFilters): Promise<SyncAnalyticsResponse> {
        const params = new URLSearchParams({
            location_id: locationId,
            emr,
            date_from: filters.dateFrom,
            date_to: filters.dateTo,
        });
        if (filters.entity) params.append('entity', filters.entity);
        if (filters.direction) params.append('direction', filters.direction);

        const response = await authFetch(`${API_BASE_URL}/sync-tracking/analytics?${params.toString()}`);
        if (response.status === 503) {
            throw new EngineUnavailableError('Engine DB is not configured');
        }
        if (!response.ok) {
            const detail = await response.json().catch(() => ({}));
            throw new Error(detail?.detail || 'Failed to load sync analytics');
        }
        return response.json();
    },

    /**
     * Breakdown for a pie slice or bar day the operator clicked.
     */
    async analyticsDetail(
        locationId: string,
        emr: string,
        filters: SyncAnalyticsDetailFilters,
    ): Promise<SyncAnalyticsDetailResponse> {
        const params = new URLSearchParams({
            location_id: locationId,
            emr,
            date_from: filters.dateFrom,
            date_to: filters.dateTo,
            scope: filters.scope,
            scope_key: filters.scopeKey,
        });
        if (filters.entity) params.append('entity', filters.entity);
        if (filters.direction) params.append('direction', filters.direction);

        const response = await authFetch(
            `${API_BASE_URL}/sync-tracking/analytics/detail?${params.toString()}`,
        );
        if (response.status === 503) {
            throw new EngineUnavailableError('Engine DB is not configured');
        }
        if (!response.ok) {
            const detail = await response.json().catch(() => ({}));
            throw new Error(detail?.detail || 'Failed to load chart detail');
        }
        return response.json();
    },

    /**
     * Push the fleet-wide Sync Findings PDF (all active practices, last 24h) to Slack.
     * Admin / full-practice-access only — same payload as the daily 11:00 IST cron.
     */
    async findingsReport(): Promise<{
        status: string;
        practices: number;
        open_issues: number;
        filename: string;
        triggered_by: string;
        window: string;
    }> {
        const response = await authFetch(`${API_BASE_URL}/sync-tracking/findings-report`, {
            method: 'POST',
        });
        if (response.status === 503) {
            throw new EngineUnavailableError('Engine DB is not configured');
        }
        if (!response.ok) {
            const detail = await response.json().catch(() => ({}));
            throw new Error(detail?.detail || 'Failed to send sync findings report');
        }
        return response.json();
    },

    /**
     * Acknowledge a Needs-attention or Failed sync item.
     * Sets status to acknowledged and stores the optional comment.
     */
    async acknowledge(
        locationId: string,
        emr: string,
        itemId: number,
        comments: string = '',
    ): Promise<SyncTrackingItem> {
        const params = new URLSearchParams({ location_id: locationId, emr });
        const response = await authFetch(
            `${API_BASE_URL}/sync-tracking/${itemId}/acknowledge?${params.toString()}`,
            {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ comments }),
            },
        );
        if (response.status === 503) {
            throw new EngineUnavailableError('Engine DB is not configured');
        }
        if (!response.ok) {
            const detail = await response.json().catch(() => ({}));
            throw new Error(detail?.detail || 'Failed to acknowledge sync item');
        }
        return response.json();
    },
};
